import React, { useEffect } from 'react';
import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import './App.css';
import PrivateRoute from './components/PrivateRoute';
import PublicRoute from './components/PublicRoute';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { extractAndStoreShareToken } from './utils/shareToken';
import Artists from './pages/Artists';
import Artist from './pages/Artist';
import Labels from './pages/Labels';
import Label from './pages/Label';
import Tracks from './pages/Tracks';
import Track from './pages/Track';
import Sets from './pages/Sets';
import Set from './pages/Set';
import Genres from './pages/Genres';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import NoSession from './pages/NoSession';
import Home from './pages/Home';
import Search from './pages/Search';
import AdminRoute from './components/AdminRoute';
import DemoCandidates from './pages/DemoCandidates';
import DemoCandidate from './pages/DemoCandidate';
import AdminActivity from './pages/AdminActivity';
import { ADMIN_PAGES } from './utils/adminPages';
import About from './pages/About';
import Terms from './pages/Terms';
import Policy from './pages/Policy';
import PlayProvider from './context/Play';
import ApiProvider from './context/ApiProvider';
import UserProvider from './context/UserProvider';
import { ThemeProvider } from './context/ThemeProvider';
import { Box } from '@mui/material';
import { SnackbarProvider } from 'notistack';

function DemoCandidateRedirect() {
  const { id } = useParams();
  return <Navigate to={`/admin/demo/candidates/${id}`} replace />;
}

function AppContent() {
  const location = useLocation();

  // Extract and store share token from URL if present
  useEffect(() => {
    extractAndStoreShareToken(window.location.href);
  }, [location]);

  return (
    <div className="App">
      <Navbar />
      <Box
        marginTop={12.25}
        maxWidth={1536}
        marginX={'auto'}
        minHeight={'calc(100vh - 300px)'}
        padding={{ xs: 0, sm: '0 24px' }}
      >
        <Routes>
          <Route
            path={'/'}
            element={
              <PublicRoute>
                <NoSession />
              </PublicRoute>
            }
          >
            <Route index element={<Navigate to={`/login`} />} />
            <Route index path="login" element={<Login />} />
            <Route path="register" element={<Register />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset" element={<ResetPassword />} />
            <Route path="about" element={<About />} />
            <Route path="policy" element={<Policy />} />
            <Route path="terms" element={<Terms />} />
          </Route>
          <Route
            path="*"
            element={
              <PrivateRoute>
                <Routes>
                  <Route path="/home/" index element={<Home />} />
                  <Route path="/artists/" element={<Artists />} />
                  <Route path="/artists/:id" element={<Artist />} />
                  <Route path="/labels/" element={<Labels />} />
                  <Route path="/labels/:id" element={<Label />} />
                  <Route path="/tracks/" element={<Tracks />} />
                  <Route path="/tracks/:id" element={<Track />} />
                  <Route path="/sets/" element={<Sets />} />
                  <Route path="/sets/:id" element={<Set />} />
                  <Route path="/genres/" element={<Genres />} />
                  <Route path="/profile/" element={<Profile />} />
                  <Route path="/search/" element={<Search />} />
                  <Route
                    path="/admin/demo/"
                    element={
                      <AdminRoute>
                        <DemoCandidates />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/demo/candidates/:id"
                    element={
                      <AdminRoute>
                        <DemoCandidate />
                      </AdminRoute>
                    }
                  />
                  {ADMIN_PAGES.filter(({ kind }) => kind).map(
                    ({ kind, url }) => (
                      <Route
                        key={kind}
                        path={`${url}/`}
                        element={
                          <AdminRoute>
                            <AdminActivity key={kind} kind={kind} />
                          </AdminRoute>
                        }
                      />
                    )
                  )}
                  <Route
                    path="/admin/"
                    element={<Navigate to="/admin/users" replace />}
                  />
                  {/* old demo links keep working */}
                  <Route
                    path="/demo/"
                    element={<Navigate to="/admin/demo" replace />}
                  />
                  <Route
                    path="/demo/candidates/:id"
                    element={<DemoCandidateRedirect />}
                  />
                  <Route path="*" element={<Navigate to="/home" />} />
                </Routes>
              </PrivateRoute>
            }
          />
        </Routes>
      </Box>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <SnackbarProvider
        maxSnack={3}
        preventDuplicate
        autoHideDuration={5000}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <ApiProvider>
          <UserProvider>
            <PlayProvider>
              <AppContent />
            </PlayProvider>
          </UserProvider>
        </ApiProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
